/**
 * "Step 6 — Missing Property Conditions" log entry.
 *
 * Built from the conditionMemory returned by /enrichment/step/extract-with-memory:
 * which properties already had an approved rule in the database, which rules the AI
 * wrote for the missing ones (model, prompt, output, timing), and what is still missing.
 */
export function describeConditionMemory(conditionMemory, { conditionModel } = {}) {
    const cm = conditionMemory || {};
    const approved = cm.approved || [];
    const drafts = cm.drafts || [];
    const stillMissing = cm.missing || [];
    const generation = cm.generation || null;
    const requested = cm.requested || [...new Set([...approved.map(a => a.propertyName), ...drafts.map(d => d.propertyName), ...stillMissing])];
    const error = cm.error || generation?.error || null;
    const model = generation?.model || conditionModel || null;

    let action;
    if (cm.error) action = `Condition lookup failed: ${cm.error}`;
    else if (!generation) action = `All ${requested.length} properties have approved rules in the database — no AI call`;
    else if (generation.error) action = `${approved.length} rule(s) from the database; AI could not write ${stillMissing.length} missing rule(s): ${generation.error}`;
    else action = `${approved.length} rule(s) from the database, ${drafts.length} written by AI (${model})${stillMissing.length ? `, ${stillMissing.length} still missing` : ''}`;

    const lines = [[`  🧠 Step 6 — Property Conditions: ${action}`, error ? 'warning' : 'detail']];
    if (drafts.length) lines.push([`     AI wrote rules for: ${drafts.map(d => d.propertyName).join(', ')}`, 'detail']);
    if (stillMissing.length) lines.push([`     No rule for: ${stillMissing.join(', ')}`, 'detail']);

    return {
        action,
        result: error ? 'warning' : 'success',
        lines,
        data: {
            nounModifier: cm.context?.nounModifier || null,
            manufacturer: cm.context?.manufacturerName || null,
            requestedProperties: requested,
            approvedFromDatabase: approved.map(a => ({ property: a.propertyName, condition: a.conditionText, source: a.source, id: a.id })),
            writtenByAI: drafts.map(d => ({ property: d.propertyName, condition: d.conditionText })),
            stillMissing,
            model,
            provider: generation?.provider || null,
            durationMs: generation?.durationMs ?? null,
            usage: generation?.usage || null,
            prompt: generation?.prompt || null,
            aiOutput: generation?.output || null,
            error,
        },
    };
}
